import { Color } from '@components/ColorSelector/ColorSelector';
import Popover, { positionBounded } from '@components/Popover/Popover';
import Slider from '@components/Slider/Slider';
import Tooltip from '@components/Tooltip/Tooltip';
import { faMinus, faPlus } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import useNode from '@hooks/use-node';
import { analytics } from '@modules/analytics';
import { Box } from '@ui-lib';
import { useCallback, useMemo } from 'react';
import { ChromePicker } from 'react-color';

import styles from './BlinqImageEditor.module.scss';
import { zoomStep } from './constants';
import { colorHexToNumber, numberToColorHex } from './helpers';

const BACKUP_COLORS = ['#ffffff'];

export const BackgroundColorSelector: React.FC<
  React.PropsWithChildren<{
    color: number;
    setColor: (c: number) => void;
    isCustomSelectOpen: boolean;
    setIsCustomSelectOpen: (open: boolean) => void;
    suggestedColors?: number[];
  }>
> = ({ color, setColor, isCustomSelectOpen, setIsCustomSelectOpen, suggestedColors }) => {
  const [buttonNode, buttonRef] = useNode<HTMLElement>();
  const hex = useMemo(() => numberToColorHex(color), [color]);

  const suggestedBgColors = suggestedColors?.map(numberToColorHex) || BACKUP_COLORS;

  const handleChooseSuggestedColor = useCallback(
    (c: string) => {
      analytics.track({
        name: 'suggestedbackgroundcolor_selected',
        properties: { module: 'image_editor' },
      });
      setColor(colorHexToNumber(c));
    },
    [setColor],
  );
  const handleChooseCustomColor = useCallback(
    (c: string) => {
      analytics.track({
        name: 'custombackgroundcolor_selected',
        properties: { module: 'image_editor' },
      });
      setColor(colorHexToNumber(c));
    },
    [setColor],
  );

  return (
    <>
      <div className={styles.controls}>
        <div className={styles['suggested-text']}> Suggested background colors</div>
        <div className={styles['background-selector']}>
          <Box
            display="flex"
            flexGrow={1}
            gap="medium"
            justifyContent={{
              desktop: 'spaceBetween',
            }}
            flexDirection={{
              mobile: 'column',
              tablet: 'column',
              desktop: 'row',
            }}
          >
            <div className={styles['suggested-colors']}>
              {suggestedBgColors.map((c) => (
                <Tooltip key={c} size="small" label={c?.toUpperCase()} aria-label="Color hex" show={true}>
                  <div>
                    <Color
                      size="lg"
                      selected={hex === c}
                      variant="border"
                      hex={c}
                      key={c}
                      onClick={() => handleChooseSuggestedColor(c)}
                    />
                  </div>
                </Tooltip>
              ))}
            </div>

            <div className={styles['bg-color-button-container']}>
              <button
                ref={buttonRef}
                className={styles['bg-color-button']}
                onClick={() => setIsCustomSelectOpen(true)}
                type="button"
              >
                <span
                  aria-label="Background Color Preview"
                  className={styles['bg-color-button-preview']}
                  style={{ backgroundColor: hex }}
                ></span>
                <div style={{ marginLeft: '12px' }}>Customize</div>
              </button>
            </div>
          </Box>
          <Popover
            positioner={positionBounded({
              offsetY: 0,
              offsetX: 7,
              alignY: 'cover',
              yPreference: 'top',
              xPreference: 'right',
            })}
            targetNode={buttonNode}
            show={isCustomSelectOpen}
            onClose={() => setIsCustomSelectOpen(false)}
            preventInteractionBubbling
          >
            <ChromePicker
              disableAlpha={true}
              color={hex}
              onChange={(color) => handleChooseCustomColor(color.hex)}
              onChangeComplete={(c) => {
                handleChooseCustomColor(c.hex);
              }}
            />
          </Popover>
        </div>
      </div>
    </>
  );
};

// components
export const ZoomControls: React.FC<
  React.PropsWithChildren<{
    defaultZoom: number;
    zoom: number;
    setZoom: (n: number) => void;
    zoomBounds?: { min: number; max: number } | null | undefined;
  }>
> = ({ zoom, setZoom, zoomBounds, defaultZoom }) => {
  const handleDecrement = useCallback(() => {
    if (zoomBounds) {
      setZoom(Math.max(zoomBounds.min, zoom - zoomStep));
      analytics.track({ name: 'editor_zoomed', properties: { module: 'image_editor' } });
    }
  }, [setZoom, zoomBounds, zoom]);
  const handleIncrement = useCallback(() => {
    if (zoomBounds) {
      setZoom(Math.min(zoomBounds.max, zoom + zoomStep));
      analytics.track({ name: 'editor_zoomed', properties: { module: 'image_editor' } });
    }
  }, [setZoom, zoomBounds, zoom]);

  return (
    <>
      <div className={styles.controls}>
        <div className={styles.zoom}>
          <button className={styles['zoom-button-left']} onClick={handleDecrement} type="button">
            <FontAwesomeIcon size="sm" icon={faMinus} />
          </button>
          <div className={styles.slider}>
            {zoomBounds && (
              <Slider
                min={zoomBounds.min}
                max={zoomBounds.max}
                value={zoom}
                resetValue={defaultZoom}
                onValueChange={(v) => {
                  setZoom(parseFloat(v.toFixed(2)));
                }}
              />
            )}
          </div>
          <button className={styles['zoom-button-right']} onClick={handleIncrement} type="button">
            <FontAwesomeIcon size="sm" icon={faPlus} />
          </button>
        </div>
      </div>
    </>
  );
};
